import UploadIcon from '@mui/icons-material/CloudUpload'
import EditIcon from '@mui/icons-material/Edit'
import type { ButtonBaseProps, ButtonProps, IconButtonProps, Theme } from '@mui/material'
import { Button, IconButton } from '@mui/material'
import { styled } from '@mui/material/styles'
import clsx from 'clsx'
import { isString } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

const primaryButtonStyles = (theme: Theme) => ({
  backgroundColor: `${theme.palette.primary.main} !important`,
  borderRadius: `${theme.tokens.btnRadius}px !important`,
  margin: '0 !important',
  color: 'var(--btn-text-color) !important',
  '&:hover': {
    filter: 'brightness(1.1)'
  },
  iconButton: {
    borderRadius: '15%'
  },
  disabled: {
    opacity: 0.4,
    cursor: 'not-allowed!important'
  }
})

const StyledButton = styled(Button)(({ theme }) => primaryButtonStyles(theme))

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  ...primaryButtonStyles(theme),
  borderRadius: '15% !important'
}))

type YisPrimaryButtonProps = ButtonBaseProps & ButtonProps & IconButtonProps & {
  buttonType?: 'edit' | 'upload'
  fullWidth?: boolean
  text?: string | React.ReactNode
  type?: string
  title?: string
  disabled?: boolean
  endIcon?: React.ReactNode
}

export default function YisPrimaryButton (props: YisPrimaryButtonProps) {
  const { t } = useTranslation()

  const { buttonType, className, ...rest } = props

  const getIcon = (type: 'edit' | 'upload' | undefined) => {
    switch (type) {
      case 'edit':
        return { component: <EditIcon />, title: 'Edit' }
      case 'upload':
        return { component: <UploadIcon />, title: 'Upload' }
      default:
        return undefined
    }
  }

  const icon = getIcon(buttonType)

  const btnText = isString(props.text) ? t(props.text) : props.text

  if (icon) {
    return <StyledIconButton
      title={t(icon.title)}
      className={className}
      onClick={props.onClick}
      {...rest}
    >
      {icon.component}
    </StyledIconButton>
  }

  return <StyledButton
    title={t(props.title)}
    variant="contained"
    className={clsx(className)}
    onClick={props.onClick}
    {...rest}
  >
    {btnText || ''}
  </StyledButton>
}
